/**
 * Labstep
 *
 * @module prosemirror/components/Menu/Commands
 * @desc A menu to select elements triggered by '/'
 */

import { withAuthenticatedUserEntity } from 'labstep-web/containers/AuthenticatedUser';
import { usePremiumFeatureStatus } from 'labstep-web/hooks/premium-feature';
import { MENU_COMMANDS_ELEMENTS } from 'labstep-web/prosemirror/components/Menu/Commands/elements';
import { MenuCommandsMenu } from 'labstep-web/prosemirror/components/Menu/Commands/Menu';
import { MenuCommandsModalAdd } from 'labstep-web/prosemirror/components/Menu/Commands/Modal/Add';
import { MenuCursor } from 'labstep-web/prosemirror/components/Menu/Cursor';
import { handleSetElements } from 'labstep-web/prosemirror/extensions/slash/commands';
import {
  getAvailableElements,
  getIsInSubmenu,
  getState,
  getSubmenuParent,
} from 'labstep-web/prosemirror/extensions/slash/commands/selectors';
import type React from 'react';
import { useEffect, useRef } from 'react';
import type {
  IMenuCommandsElementProps,
  IMenuCommandsProps,
} from './types';

export enum MenuCommandsLabel {
  New = 'New',
}

export enum MenuCommandsSection {
  Common = 'Common',
  Tables = 'Tables',
  InputFields = 'Input Fields',
  OutputFields = 'Output Fields',
  DomainTools = 'Domain Tools',
  Code = 'Code',
}

export const MenuCommands: React.FC<IMenuCommandsProps> = ({
  state,
  dispatch,
  view,
  entity,
  experimentWorkflow,
  protocolCollection,
  cursorPosition,
}) => {
  const { isOpen, submittedElement, index } = getState(state);
  const availableElements = getAvailableElements(state);
  const isInSubmenu = getIsInSubmenu(state);
  const submenuParent = getSubmenuParent(state);

  // Create separate refs for submenu
  const submenuRef = useRef<HTMLElement>(null);
  const submenuActiveItemRef = useRef<HTMLElement>(null);

  useEffect(() => {
    handleSetElements(state, dispatch, entity, experimentWorkflow);
  }, [isOpen]);

  const menuCommandElements = usePremiumFeatureStatus(
    MENU_COMMANDS_ELEMENTS,
    'id',
    'slash_commands',
  );

  // Get main menu elements
  const getElementsFromIds = (ids: string[]) => {
    return ids
      .map((id) =>
        menuCommandElements.find((element) => element.id === id),
      )
      .filter(
        (element) => element !== undefined,
      ) as IMenuCommandsElementProps[];
  };

  // Get main menu elements
  const mainMenuElements = getElementsFromIds(availableElements);

  // Get submenu elements (availableElements from selector shows submenu when in submenu mode)
  const submenuElements = isInSubmenu
    ? getElementsFromIds(availableElements) // availableElements contains submenu items when in submenu
    : [];

  return (
    <div>
      <MenuCursor cursorPosition={cursorPosition} isOpen={isOpen}>
        {({ menuRef, activeItemRef }) => (
          <>
            {!isInSubmenu && (
              <MenuCommandsMenu
                topLevelPermissionEntity={
                  experimentWorkflow ||
                  protocolCollection ||
                  ({} as any)
                }
                ref={menuRef}
                activeItemRef={
                  isInSubmenu ? undefined : activeItemRef
                }
                state={state}
                dispatch={dispatch}
                elements={mainMenuElements}
                index={isInSubmenu ? -1 : index}
              />
            )}
            {isInSubmenu && (
              <MenuCommandsMenu
                topLevelPermissionEntity={
                  experimentWorkflow ||
                  protocolCollection ||
                  ({} as any)
                }
                ref={submenuRef}
                activeItemRef={submenuActiveItemRef}
                state={state}
                dispatch={dispatch}
                elements={submenuElements}
                index={index}
              />
            )}
          </>
        )}
      </MenuCursor>
      <MenuCommandsModalAdd
        state={state}
        dispatch={dispatch}
        view={view}
        entity={entity}
        experimentWorkflow={experimentWorkflow!}
        submittedElement={menuCommandElements.find(
          (element) => element.id === submittedElement,
        )}
      />
    </div>
  );
};

export default withAuthenticatedUserEntity(MenuCommands);
